import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { BRAND, SERVICES } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const goContact = (e) => {
    e?.preventDefault?.();
    if (location.pathname !== "/") {
      navigate("/#contact");
      setTimeout(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    } else {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="main-nav"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#fdfbf7]/85 border-b border-[#e7e5df] shadow-[0_2px_20px_-10px_rgba(0,0,0,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="nav-home-link"
          >
            <img
              src={BRAND.logo}
              alt="ProCraft Solutions"
              className="h-12 w-12 rounded-xl object-cover ring-1 ring-[#e7e5df] group-hover:ring-[#16a34a] transition"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-black text-[#1c1917] text-lg">
                ProCraft <span className="text-[#16a34a]">Solutions</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#57534e] font-bold">
                Build Smart. From The Start.
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-semibold text-[#1c1917] hover:text-[#16a34a] transition"
              data-testid="nav-link-home"
            >
              Home
            </Link>
            <a
              href="/#about"
              className="text-sm font-semibold text-[#1c1917] hover:text-[#16a34a] transition"
              data-testid="nav-link-about"
            >
              About
            </a>

            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-sm font-semibold text-[#1c1917] hover:text-[#16a34a] transition"
                data-testid="nav-services-trigger"
                onClick={() => setServicesOpen((v) => !v)}
              >
                Services <ChevronDown className="w-4 h-4" />
              </button>
              {servicesOpen && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                  data-testid="nav-services-menu"
                >
                  <div className="bg-white border border-[#e7e5df] rounded-2xl shadow-xl p-2 w-72">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className="block px-4 py-2.5 rounded-xl text-sm font-semibold text-[#1c1917] hover:bg-[#f4f1ea] hover:text-[#16a34a] transition"
                        data-testid={`nav-service-${s.slug}`}
                      >
                        {s.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="/#gallery"
              className="text-sm font-semibold text-[#1c1917] hover:text-[#16a34a] transition"
              data-testid="nav-link-gallery"
            >
              Gallery
            </a>
            <a
              href="/#reviews"
              className="text-sm font-semibold text-[#1c1917] hover:text-[#16a34a] transition"
              data-testid="nav-link-reviews"
            >
              Reviews
            </a>
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={BRAND.phoneHref}
              data-testid="nav-phone-cta"
              className="hidden md:inline-flex items-center gap-2 px-4 h-11 rounded-full border-2 border-[#1c1917] text-[#1c1917] font-bold text-sm hover:bg-[#1c1917] hover:text-white transition-all duration-300"
            >
              <Phone className="w-4 h-4" />
              {BRAND.phone}
            </a>
            <button
              onClick={goContact}
              data-testid="nav-contact-cta"
              className="hidden sm:inline-flex items-center px-5 h-11 rounded-full bg-[#16a34a] hover:bg-[#15803d] text-white font-bold text-sm transition-all duration-300 active:scale-95 shadow-[0_4px_14px_rgba(22,163,74,0.35)]"
            >
              Contact Now
            </button>
            <button
              className="lg:hidden p-2 rounded-lg border border-[#e7e5df] bg-white"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            className="lg:hidden pb-6 pt-2 border-t border-[#e7e5df]"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1">
              <Link to="/" className="px-3 py-3 rounded-xl font-semibold hover:bg-[#f4f1ea]">
                Home
              </Link>
              <a href="/#about" className="px-3 py-3 rounded-xl font-semibold hover:bg-[#f4f1ea]">
                About
              </a>
              <div className="px-3 py-2 text-xs uppercase tracking-widest text-[#57534e] font-bold">
                Services
              </div>
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="px-5 py-2.5 rounded-xl text-sm hover:bg-[#f4f1ea]"
                  data-testid={`nav-mobile-service-${s.slug}`}
                >
                  {s.title}
                </Link>
              ))}
              <a href="/#gallery" className="px-3 py-3 rounded-xl font-semibold hover:bg-[#f4f1ea]">
                Gallery
              </a>
              <a href="/#reviews" className="px-3 py-3 rounded-xl font-semibold hover:bg-[#f4f1ea]">
                Reviews
              </a>
              <a
                href={BRAND.phoneHref}
                className="mt-3 inline-flex justify-center items-center gap-2 h-12 rounded-full border-2 border-[#1c1917] font-bold"
                data-testid="nav-mobile-phone"
              >
                <Phone className="w-4 h-4" />
                {BRAND.phone}
              </a>
              <button
                onClick={goContact}
                className="mt-2 inline-flex justify-center items-center h-12 rounded-full bg-[#16a34a] text-white font-bold"
                data-testid="nav-mobile-contact"
              >
                Contact Now
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
